import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { confirmDialog } from '../components/ui/ImpulzaDialog';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Box,
  Check,
  DollarSign,
  FlaskConical,
  Hammer,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import { getLocalYMD } from '../utils/storeLogistics';
import {
  findRecipeDefinitionForProduct,
  loadRecipeBomForSite,
  loadRelationalRecipesIfAvailable,
} from '../utils/productionRecipes';
import { formatCurrency } from '../utils/numberFormat';
import { applyActiveProductsFilter } from '../utils/productStatus';

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

const lsKey = (orgId, key) => `impulza:${orgId || 'noorg'}:${key}`;
const safeLSGet = (orgId, key, fallback = '') => {
  try { return localStorage.getItem(lsKey(orgId, key)) || fallback; } catch { return fallback; }
};
const safeLSSet = (orgId, key, value) => {
  try { localStorage.setItem(lsKey(orgId, key), value || ''); } catch { /* storage no disponible */ }
};

const normalizeDepositoNombre = (value) => String(value || '').trim();

const formatCantidad = (value) => Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

/**
 * Produccion masiva: misma logica de negocio que ProduccionRegistrar pero para N
 * productos en una sola tanda. Origen y destino son globales para toda la corrida.
 *
 * Optimizacion clave: el BOM de cada linea se pide UNA sola vez con quantity = 1
 * (loadRecipeBomForSite escala linealmente por cantidad/rendimiento), y despues se
 * escala en el cliente al cambiar la cantidad. Asi evitamos N queries por tecla.
 */
function ProduccionMasiva({ onNavigate, sitioId }) {
  const { profile, organization } = useAuth();
  const navigate = useNavigate();
  const orgIdForLs = profile?.organization_id || '';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productos, setProductos] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recetasNuevas, setRecetasNuevas] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [enableMerma, setEnableMerma] = useState(false);
  // Logico de empresa (EmpresaDatos): produce aunque el insumo no alcance y
  // deja el insumo en negativo en lugar de bloquear la tanda.
  const [allowWithoutStock, setAllowWithoutStock] = useState(false);

  const [operationalDate, setOperationalDate] = useState(getLocalYMD(new Date()));
  const [sitioOrigenId, setSitioOrigenId] = useState(safeLSGet(orgIdForLs, 'last_sitio_origen') || sitioId || '');
  const [depositoOrigen, setDepositoOrigen] = useState(safeLSGet(orgIdForLs, 'last_deposito_origen'));
  const [sitioDestinoId, setSitioDestinoId] = useState(safeLSGet(orgIdForLs, 'last_sitio_destino') || sitioId || '');
  const [depositoDestino, setDepositoDestino] = useState(safeLSGet(orgIdForLs, 'last_deposito_destino'));

  const [query, setQuery] = useState('');
  const [lineas, setLineas] = useState([]);
  const [cantidadGlobal, setCantidadGlobal] = useState('');
  const [progreso, setProgreso] = useState({ hecho: 0, total: 0 });

  const fmtMoney = (n) => formatCurrency(n, { orgId: profile?.organization_id });

  useEffect(() => {
    if (profile?.organization_id) fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;
      const [prodsRes, sitsRes, recetasJsonRes] = await Promise.all([
        // Productos a producir e insumos: sin filtro de estado aparecian los
        // dados de baja como si se pudieran producir o consumir.
        applyActiveProductsFilter(
          supabase.from('products').select('*, units_of_measure(abbreviation)').eq('organization_id', orgId)
        ),
        supabase.from('sitios').select('*').eq('organization_id', orgId),
        supabase.from('recetas').select('*').eq('organization_id', orgId),
      ]);

      if (prodsRes.error) throw prodsRes.error;
      if (sitsRes.error) throw sitsRes.error;
      if (recetasJsonRes.error) throw recetasJsonRes.error;

      setEnableMerma(Boolean(organization?.enable_recipe_merma));
      setAllowWithoutStock(Boolean(organization?.allow_production_without_stock));

      const recetasRows = recetasJsonRes.data || [];
      const relationalRecipes =
        recetasRows.length > 0
          ? []
          : await loadRelationalRecipesIfAvailable({
              supabase,
              organizationId: orgId,
              select: '*, yield, recipe_items(*)',
            });

      setProductos(prodsRes.data || []);
      setRecipes(relationalRecipes);
      setSitios(sitsRes.data || []);
      setRecetasNuevas(recetasRows);
    } catch (error) {
      console.error('[ProduccionMasiva] fetchInitialData', error);
      toast.error('Error al cargar datos maestros');
    } finally {
      setLoading(false);
    }
  };

  const depositosOrigenLista = sitios.find((s) => s.id === sitioOrigenId)?.depositos || [];
  const depositosDestinoLista = sitios.find((s) => s.id === sitioDestinoId)?.depositos || [];

  // Solo tiene sentido producir lo que tiene receta definida.
  const productosConReceta = useMemo(() => {
    if (productos.length === 0) return [];
    return productos.filter((product) =>
      Boolean(findRecipeDefinitionForProduct({
        productId: product.id,
        recetasJson: recetasNuevas,
        recipes,
      })),
    );
  }, [productos, recetasNuevas, recipes]);

  const seleccionadosIds = useMemo(
    () => new Set(lineas.map((linea) => linea.producto.id)),
    [lineas],
  );

  const resultadosBusqueda = useMemo(() => {
    const term = query.trim().toLowerCase();
    const base = term
      ? productosConReceta.filter((product) => {
          const name = (product.name || '').toLowerCase();
          const barcode = (product.barcode || '').toLowerCase();
          return name.includes(term) || barcode.includes(term);
        })
      : productosConReceta;
    return base.slice(0, 80);
  }, [productosConReceta, query]);

  /**
   * Carga el BOM unitario (para 1 unidad) de un producto en el sitio de origen.
   * El resultado se guarda en la linea y despues se escala en cliente.
   */
  const cargarBomUnitario = useCallback(async (producto) => {
    const result = await loadRecipeBomForSite({
      supabase,
      productId: producto.id,
      siteId: sitioOrigenId,
      quantity: 1,
      recetasJson: recetasNuevas,
      recipes,
      applyMerma: enableMerma,
    });
    return result;
  }, [enableMerma, recetasNuevas, recipes, sitioOrigenId]);

  const agregarProducto = async (producto) => {
    if (seleccionadosIds.has(producto.id)) {
      toast('Ese producto ya esta en la tanda', { icon: 'ℹ️' });
      return;
    }
    if (!sitioOrigenId) {
      toast.error('Elegi primero la sucursal de origen');
      return;
    }

    const nuevaLinea = {
      key: producto.id,
      producto,
      cantidad: cantidadGlobal || '1',
      observaciones: '',
      bomUnit: [],
      recipeYield: 1,
      mermaApplied: false,
      mermaPct: 0,
      cargando: true,
      errorBom: '',
      idempotencyKey: generateIdempotencyKey(),
    };
    setLineas((prev) => [...prev, nuevaLinea]);

    try {
      const result = await cargarBomUnitario(producto);
      setLineas((prev) => prev.map((linea) => (
        linea.key === producto.id
          ? {
              ...linea,
              bomUnit: result.hasRecipe ? result.bom : [],
              recipeYield: Number(result.recipe?.yield) || 1,
              mermaApplied: Boolean(result.mermaApplied),
              mermaPct: Number(result.mermaPct || 0),
              cargando: false,
              errorBom: result.hasRecipe ? '' : 'Sin receta',
            }
          : linea
      )));
    } catch (error) {
      console.error('[ProduccionMasiva] cargarBom', error);
      setLineas((prev) => prev.map((linea) => (
        linea.key === producto.id
          ? { ...linea, cargando: false, errorBom: 'Error al cargar receta' }
          : linea
      )));
    }
  };

  // Si cambia el sitio de origen, el stock de los insumos cambia: recargamos todo.
  useEffect(() => {
    if (!sitioOrigenId || lineas.length === 0) return;
    let cancelado = false;

    (async () => {
      setLineas((prev) => prev.map((linea) => ({ ...linea, cargando: true })));
      const actualizadas = await Promise.all(
        lineas.map(async (linea) => {
          try {
            const result = await cargarBomUnitario(linea.producto);
            return {
              ...linea,
              bomUnit: result.hasRecipe ? result.bom : [],
              recipeYield: Number(result.recipe?.yield) || 1,
              mermaApplied: Boolean(result.mermaApplied),
              mermaPct: Number(result.mermaPct || 0),
              cargando: false,
              errorBom: result.hasRecipe ? '' : 'Sin receta',
            };
          } catch {
            return { ...linea, cargando: false, errorBom: 'Error al cargar receta' };
          }
        }),
      );
      if (!cancelado) setLineas(actualizadas);
    })();

    return () => { cancelado = true; };
    // Solo depende del sitio de origen: no queremos recargar al tipear cantidades.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitioOrigenId]);

  const quitarLinea = (key) => setLineas((prev) => prev.filter((linea) => linea.key !== key));
  const limpiarTabla = () => setLineas([]);

  const actualizarLinea = (key, patch) => {
    setLineas((prev) => prev.map((linea) => (linea.key === key ? { ...linea, ...patch } : linea)));
  };

  const aplicarCantidadATodos = () => {
    const value = Number(cantidadGlobal);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Ingresa una cantidad valida para aplicar');
      return;
    }
    setLineas((prev) => prev.map((linea) => ({ ...linea, cantidad: String(value) })));
  };

  // Escalado en cliente del BOM unitario + costos por linea.
  const lineasCalculadas = useMemo(() => {
    return lineas.map((linea) => {
      const cantidadNumerica = Number(linea.cantidad || 0);
      const cantidadValida = Number.isFinite(cantidadNumerica) && cantidadNumerica > 0;
      const bom = linea.bomUnit.map((item) => ({
        ...item,
        necesario: Number(item.necesario || 0) * (cantidadValida ? cantidadNumerica : 0),
      }));
      const costoTotal = bom.reduce(
        (acc, item) => acc + Number(item.costoUnitarioInsumo || 0) * Number(item.necesario || 0),
        0,
      );
      const merma = linea.mermaApplied ? Number(linea.mermaPct || 0) : 0;
      const divisor = cantidadNumerica * (1 - merma / 100);
      const costoUnitario = divisor > 0 ? costoTotal / divisor : 0;

      let error = '';
      if (linea.errorBom) error = linea.errorBom;
      else if (!cantidadValida) error = 'Cantidad invalida';
      else if (bom.length === 0 && !linea.cargando) error = 'Receta sin insumos';

      return { ...linea, cantidadNumerica, cantidadValida, bom, costoTotal, costoUnitario, error };
    });
  }, [lineas]);

  /**
   * Consolidado de insumos: dos productos distintos pueden compartir el mismo
   * insumo, asi que la validacion real de stock se hace sobre la suma de la tanda,
   * no linea por linea.
   */
  const insumosConsolidados = useMemo(() => {
    const mapa = new Map();
    lineasCalculadas.forEach((linea) => {
      linea.bom.forEach((item) => {
        // Insumos de solo costo (mano de obra, agua): no participan del control de stock.
        if (item.descuentaStock === false) return;
        const key = String(item.id);
        const previo = mapa.get(key);
        if (previo) {
          previo.requerido += Number(item.necesario || 0);
        } else {
          mapa.set(key, {
            id: item.id,
            nombre: item.nombre,
            um: item.um,
            requerido: Number(item.necesario || 0),
            stockActual: Number(item.stockActual || 0),
          });
        }
      });
    });
    return Array.from(mapa.values())
      .map((item) => ({ ...item, falta: item.stockActual < item.requerido }))
      .sort((a, b) => Number(b.falta) - Number(a.falta) || a.nombre.localeCompare(b.nombre, 'es'));
  }, [lineasCalculadas]);

  const insumosFaltantes = insumosConsolidados.filter((item) => item.falta);

  const totales = useMemo(() => {
    const costoTotal = lineasCalculadas.reduce((acc, linea) => acc + Number(linea.costoTotal || 0), 0);
    const unidades = lineasCalculadas.reduce((acc, linea) => acc + (linea.cantidadValida ? linea.cantidadNumerica : 0), 0);
    const conError = lineasCalculadas.filter((linea) => linea.error).length;
    return { costoTotal, unidades, conError, total: lineasCalculadas.length };
  }, [lineasCalculadas]);

  const cargandoAlgunBom = lineasCalculadas.some((linea) => linea.cargando);

  const canSubmit = Boolean(
    sitioOrigenId
    && sitioDestinoId
    && operationalDate
    && lineasCalculadas.length > 0
    && totales.conError === 0
    && (allowWithoutStock || insumosFaltantes.length === 0)
    && !cargandoAlgunBom
    && !saving
    && !loading,
  );

  const handleGuardar = async () => {
    if (!canSubmit) return;

    const depOrigenNorm = normalizeDepositoNombre(depositoOrigen);
    const depDestinoNorm = normalizeDepositoNombre(depositoDestino);
    if (depOrigenNorm && !depositosOrigenLista.map(normalizeDepositoNombre).includes(depOrigenNorm)) {
      toast.error('El deposito de origen no pertenece al sitio seleccionado.');
      return;
    }
    if (depDestinoNorm && !depositosDestinoLista.map(normalizeDepositoNombre).includes(depDestinoNorm)) {
      toast.error('El deposito de destino no pertenece al sitio seleccionado.');
      return;
    }

    const sitioOrigenNombre = sitios.find((s) => s.id === sitioOrigenId)?.nombre || '-';
    const sitioDestinoNombre = sitios.find((s) => s.id === sitioDestinoId)?.nombre || '-';
    const confirmacion = await confirmDialog({
      title: 'Registrar producción masiva',
      message: `Vas a registrar ${lineasCalculadas.length} producción(es) por ${formatCantidad(totales.unidades)} unidad(es) en total.`,
      details: [
        { label: 'Origen (consumo)', value: sitioOrigenNombre },
        { label: 'Destino (PT)', value: sitioDestinoNombre },
        { label: 'Fecha operativa', value: operationalDate },
      ],
      list: {
        title: 'Productos',
        items: lineasCalculadas.slice(0, 5).map((linea) => `${linea.producto.name}: ${linea.cantidadNumerica}`),
        more: Math.max(0, lineasCalculadas.length - 5),
      },
      warning: insumosFaltantes.length > 0 ? {
        title: 'Estos insumos van a quedar en negativo',
        items: insumosFaltantes.slice(0, 5).map((it) => `${it.nombre}: ${formatCantidad(it.stockActual - it.requerido)} ${it.um}`),
        more: Math.max(0, insumosFaltantes.length - 5),
      } : null,
      confirmLabel: 'Registrar',
    });
    if (!confirmacion) return;

    setSaving(true);
    setProgreso({ hecho: 0, total: lineasCalculadas.length });

    const orgId = profile.organization_id;
    const okKeys = [];
    const fallidas = [];

    // Secuencial a proposito: cada RPC descuenta stock, y varios productos pueden
    // compartir insumos. En paralelo nos arriesgamos a pisar el stock entre lineas.
    for (const linea of lineasCalculadas) {
      const payload = {
        organization_id: orgId,
        product_id: linea.producto.id,
        product_name: linea.producto.name || 'Producto',
        quantity: linea.cantidadNumerica,
        source_site_id: sitioOrigenId,
        source_deposito_id: depOrigenNorm || null,
        target_site_id: sitioDestinoId,
        target_deposito_id: depDestinoNorm || null,
        ingredientes_usados: linea.bom,
        observaciones: linea.observaciones,
        cost_unitario: linea.costoUnitario,
        cost_total: linea.costoTotal,
        operational_date: operationalDate,
        source_type: 'manual',
        production_request_line_ids: [],
        idempotency_key: linea.idempotencyKey,
      };

      try {
        const rpcResponse = await supabase.rpc('register_production_entry', { p_payload: payload });
        if (rpcResponse.error) throw rpcResponse.error;
        okKeys.push(linea.key);
      } catch (error) {
        const msg = String(error?.message || '');
        fallidas.push({ key: linea.key, nombre: linea.producto.name, msg });
        console.error('[ProduccionMasiva] linea fallida', linea.producto.name, error);
      } finally {
        setProgreso((prev) => ({ ...prev, hecho: prev.hecho + 1 }));
      }
    }

    safeLSSet(orgId, 'last_sitio_origen', sitioOrigenId);
    safeLSSet(orgId, 'last_deposito_origen', depOrigenNorm);
    safeLSSet(orgId, 'last_sitio_destino', sitioDestinoId);
    safeLSSet(orgId, 'last_deposito_destino', depDestinoNorm);

    // Las lineas OK salen de la tabla; las fallidas quedan para reintentar con
    // una idempotency_key nueva (la anterior pudo quedar consumida en el RPC).
    setLineas((prev) => prev
      .filter((linea) => !okKeys.includes(linea.key))
      .map((linea) => ({ ...linea, idempotencyKey: generateIdempotencyKey() })));

    if (fallidas.length === 0) {
      toast.success(`${okKeys.length} produccion(es) registradas`);
    } else {
      toast.error(
        `${okKeys.length} ok / ${fallidas.length} con error: ${fallidas.slice(0, 2).map((f) => f.nombre).join(', ')}`,
      );
    }

    setSaving(false);
    setProgreso({ hecho: 0, total: 0 });
  };

  const volver = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('produccion-registrar');
      return;
    }
    navigate('/sistema/produccion-registrar');
  };

  if (loading) {
    return (
      <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
          <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
            <RefreshCcw className="text-slate-300 animate-spin" size={18} />
          </div>
          <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Hammer size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Produccion masiva
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Varios productos en una sola tanda, con insumos consolidados
            </p>
            {allowWithoutStock && (
              <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-100 text-[8px] font-black uppercase tracking-widest">
                <AlertCircle size={10} /> Produccion sin stock habilitada
              </span>
            )}
          </div>
        </div>

        <div className="imp-header-actions flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={volver}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Produccion simple
          </button>
          <button
            type="button"
            onClick={limpiarTabla}
            disabled={lineas.length === 0 || saving}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            <X size={14} /> Vaciar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={!canSubmit}
            className="px-5 py-2.5 rounded-2xl bg-[#0B2A36] text-white shadow-lg hover:shadow-xl hover:opacity-95 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCcw size={16} className="animate-spin" /> {progreso.hecho}/{progreso.total}
              </>
            ) : (
              <>
                <Save size={16} strokeWidth={3} className="text-[#3FF2AA]" /> Registrar {lineas.length > 0 ? `(${lineas.length})` : ''}
              </>
            )}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo total insumos</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmtMoney(totales.costoTotal)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Toda la tanda</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Unidades a producir</p>
            <Box size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{formatCantidad(totales.unidades)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{totales.total} producto(s)</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Insumos sin stock</p>
            <AlertCircle size={16} className={
              insumosFaltantes.length === 0 ? 'text-slate-300' : allowWithoutStock ? 'text-amber-500' : 'text-rose-500'
            } />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${
            insumosFaltantes.length === 0 ? 'text-slate-800' : allowWithoutStock ? 'text-amber-600' : 'text-rose-600'
          }`}>
            {insumosFaltantes.length}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {insumosFaltantes.length > 0 && allowWithoutStock ? 'Quedan en negativo' : 'Consolidado del origen'}
          </p>
        </div>
      </div>

      {/* PARAMETROS GLOBALES */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-3">
        <h2 className="text-[10px] font-black text-[#51A7FD] uppercase tracking-widest mb-4 flex items-center gap-2">
          <Settings size={14} /> Parametros de la tanda
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2 space-y-1">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha operativa</label>
            <input
              type="date"
              value={operationalDate}
              onChange={(event) => setOperationalDate(event.target.value)}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner"
            />
          </div>

          <div className="md:col-span-3 space-y-1">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Sucursal origen (consumo)</label>
            <select
              value={sitioOrigenId}
              onChange={(event) => { setSitioOrigenId(event.target.value); setDepositoOrigen(''); }}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Seleccionar...</option>
              {sitios.map((site) => (
                <option key={site.id} value={site.id}>{site.nombre}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 space-y-1">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Deposito origen</label>
            <select
              value={depositoOrigen}
              onChange={(event) => setDepositoOrigen(event.target.value)}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Sin deposito</option>
              {depositosOrigenLista.map((dep) => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3 space-y-1">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <ArrowRight size={10} /> Sucursal destino (PT)
            </label>
            <select
              value={sitioDestinoId}
              onChange={(event) => { setSitioDestinoId(event.target.value); setDepositoDestino(''); }}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Seleccionar...</option>
              {sitios.map((site) => (
                <option key={site.id} value={site.id}>{site.nombre}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 space-y-1">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Deposito destino</label>
            <select
              value={depositoDestino}
              onChange={(event) => setDepositoDestino(event.target.value)}
              className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner cursor-pointer"
            >
              <option value="">Sin deposito</option>
              {depositosDestinoLista.map((dep) => (
                <option key={dep} value={dep}>{dep}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        {/* SELECCION DE PRODUCTOS */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <FlaskConical size={14} className="text-[#51A7FD]" /> Productos con receta
          </h2>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="BUSCAR PRODUCTO..."
              className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none focus:border-[#51A7FD] shadow-inner uppercase"
            />
          </div>

          <p className="mt-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
            {resultadosBusqueda.length} de {productosConReceta.length} con receta
          </p>

          <div className="mt-2 max-h-[420px] overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
            {resultadosBusqueda.map((product) => {
              const yaEsta = seleccionadosIds.has(product.id);
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => agregarProducto(product)}
                  disabled={yaEsta}
                  className={`w-full text-left px-3 py-2.5 transition-all flex items-center justify-between gap-2 ${
                    yaEsta ? 'bg-emerald-50/60 cursor-default' : 'hover:bg-blue-50'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                      {product.name}
                    </div>
                    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                      {product.barcode || 'S/COD'}
                    </div>
                  </div>
                  {yaEsta ? <Check size={14} className="text-emerald-600 shrink-0" /> : <Plus size={14} className="text-slate-300 shrink-0" />}
                </button>
              );
            })}

            {productosConReceta.length === 0 && (
              <div className="px-3 py-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-300">
                No hay productos con receta cargada
              </div>
            )}
          </div>
        </div>

        {/* TABLA DE PRODUCCION */}
        <div className="xl:col-span-2 space-y-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Hammer size={14} className="text-[#51A7FD]" /> A producir
              </h2>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cantidadGlobal}
                  onChange={(event) => setCantidadGlobal(event.target.value)}
                  placeholder="CANT."
                  className="w-20 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-black text-right outline-none focus:border-[#51A7FD]"
                />
                <button
                  type="button"
                  onClick={aplicarCantidadATodos}
                  disabled={lineas.length === 0}
                  className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 border border-slate-200 text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
                >
                  Aplicar a todos
                </button>
              </div>
            </div>

            {lineasCalculadas.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">
                  Elegi productos de la izquierda para armar la tanda
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead className="bg-slate-50">
                    <tr className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                      <th className="text-left px-4 py-3">Producto</th>
                      <th className="text-right px-3 py-3">Rinde</th>
                      <th className="text-right px-3 py-3">Cantidad</th>
                      <th className="text-right px-3 py-3">Costo unit.</th>
                      <th className="text-right px-3 py-3">Costo total</th>
                      <th className="text-left px-3 py-3">Observaciones</th>
                      <th className="px-3 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {lineasCalculadas.map((linea) => (
                      <tr key={linea.key} className={linea.error ? 'bg-rose-50/40' : ''}>
                        <td className="px-4 py-2.5 min-w-[200px]">
                          <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 truncate">
                            {linea.producto.name}
                          </div>
                          {linea.cargando ? (
                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-300 mt-1 flex items-center gap-1">
                              <RefreshCcw size={10} className="animate-spin" /> Calculando receta
                            </div>
                          ) : linea.error ? (
                            <div className="text-[9px] font-black uppercase tracking-widest text-rose-600 mt-1">
                              {linea.error}
                            </div>
                          ) : (
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                              {linea.bom.length} insumo(s)
                              {linea.mermaApplied ? ` · merma ${linea.mermaPct}%` : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-500">
                          {formatCantidad(linea.recipeYield)}
                        </td>
                        <td className="px-3 py-2.5">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={linea.cantidad}
                            onFocus={(event) => event.target.select()}
                            onChange={(event) => actualizarLinea(linea.key, { cantidad: event.target.value })}
                            className={`w-24 bg-slate-50 border px-2 py-2 rounded-xl text-[11px] font-black text-right outline-none focus:border-[#51A7FD] ${
                              linea.error ? 'border-rose-200' : 'border-slate-100'
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-600 whitespace-nowrap">
                          {fmtMoney(linea.costoUnitario)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-[11px] font-black text-slate-800 whitespace-nowrap">
                          {fmtMoney(linea.costoTotal)}
                        </td>
                        <td className="px-3 py-2.5">
                          <input
                            type="text"
                            value={linea.observaciones}
                            onChange={(event) => actualizarLinea(linea.key, { observaciones: event.target.value })}
                            placeholder="Opcional"
                            className="w-40 bg-slate-50 border border-slate-100 px-2 py-2 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD]"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => quitarLinea(linea.key)}
                            className="p-2 rounded-xl text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all"
                            title="Quitar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* INSUMOS CONSOLIDADOS */}
          {insumosConsolidados.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                <h2 className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Box size={14} className="text-[#51A7FD]" /> Insumos consolidados a descontar
                </h2>
                {insumosFaltantes.length > 0 && (
                  <span className={`px-3 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${
                    allowWithoutStock
                      ? 'bg-amber-50 text-amber-700 border-amber-100'
                      : 'bg-rose-50 text-rose-700 border-rose-100'
                  }`}>
                    {insumosFaltantes.length} {allowWithoutStock ? 'quedan en negativo' : 'sin stock'}
                  </span>
                )}
              </div>

              <div className="max-h-64 overflow-y-auto">
                <table className="w-full min-w-[520px]">
                  <thead className="bg-slate-50">
                    <tr className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                      <th className="text-left px-4 py-2.5">Insumo</th>
                      <th className="text-right px-3 py-2.5">Requerido</th>
                      <th className="text-right px-3 py-2.5">Stock en origen</th>
                      <th className="text-right px-4 py-2.5">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {insumosConsolidados.map((item) => (
                      <tr key={item.id} className={item.falta ? (allowWithoutStock ? 'bg-amber-50/40' : 'bg-rose-50/40') : ''}>
                        <td className="px-4 py-2 text-[10px] font-bold uppercase text-slate-700 truncate">{item.nombre}</td>
                        <td className="px-3 py-2 text-right text-[10px] font-black text-slate-800 whitespace-nowrap">
                          {formatCantidad(item.requerido)} {item.um}
                        </td>
                        <td className="px-3 py-2 text-right text-[10px] font-bold text-slate-500 whitespace-nowrap">
                          {formatCantidad(item.stockActual)} {item.um}
                        </td>
                        <td className={`px-4 py-2 text-right text-[10px] font-black whitespace-nowrap ${
                          item.falta ? (allowWithoutStock ? 'text-amber-600' : 'text-rose-600') : 'text-emerald-700'
                        }`}>
                          {formatCantidad(item.stockActual - item.requerido)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ProduccionMasiva;
